import Big from 'big.js';

/**
 * Money is always an integer number of minor units (para for RSD, cents for EUR).
 * Exchange rates are integers scaled by RATE_SCALE (4 decimal places): 117,15 → 1_171_500.
 * See docs/development/domain-model.md §1 and design-system.md §3.
 */

export const CURRENCIES = ['RSD', 'EUR'] as const;
export type Currency = (typeof CURRENCIES)[number];
export type NumberLocale = 'sr-Latn' | 'en';

export const MINOR = 100;
export const RATE_SCALE = 10_000;
export const MINUS = '−';
/** Largest magnitude accepted from a user or an API client: 100 billion major units. */
export const MAX_MINOR = 10_000_000_000_000;
const ROUND_HALF_UP = 1; // big.js rounds the magnitude, so this is "half away from zero"
const ROUND_DOWN = 0;

const SEPARATORS: Record<NumberLocale, { group: string; decimal: string }> = {
  'sr-Latn': { group: '.', decimal: ',' },
  en: { group: ',', decimal: '.' },
};

export const decimalSeparator = (locale: NumberLocale) => SEPARATORS[locale].decimal;

/** Round half away from zero: a/b for integers. */
export function divRound(a: bigint, b: bigint): bigint {
  const neg = a < 0n !== b < 0n;
  const absA = a < 0n ? -a : a;
  const absB = b < 0n ? -b : b;
  const q = (absA * 2n + absB) / (absB * 2n);
  return neg ? -q : q;
}

/** Convert a native amount to base using a rate scaled by RATE_SCALE. Round half away from zero. */
export function convert(minor: number, rate4: number): number {
  return Number(divRound(BigInt(minor) * BigInt(rate4), BigInt(RATE_SCALE)));
}

/** Exact inverse of a scaled rate: RATE_SCALE² / rate4, rounded half away from zero. */
export function invertRate(rate4: number): number {
  return Number(divRound(BigInt(RATE_SCALE) * BigInt(RATE_SCALE), BigInt(rate4)));
}

/** Round any decimal amount of minor units (a float, a string or a Big) to a whole number, half away from zero. */
export function toMinor(value: Big.BigSource): number {
  return Number(new Big(value).round(0, ROUND_HALF_UP).toFixed(0));
}

/** A decimal rate (117.1234) as the scaled integer form, rounded half away from zero. */
export function toScaledRate(rate: number): number {
  return toMinor(new Big(rate).times(RATE_SCALE));
}

/** Implied rate (scaled) from two absolute amounts: |to| / |from|. */
export function impliedRate(fromMinor: number, toMinor: number): number {
  if (fromMinor === 0) return 0;
  return Number(divRound(BigInt(Math.abs(toMinor)) * BigInt(RATE_SCALE), BigInt(Math.abs(fromMinor))));
}

/** Round a minor amount to whole major units (half away from zero), still expressed in minor units. */
export function roundToMajor(minor: number): number {
  const major = toMinor(new Big(Math.abs(minor)).div(MINOR)) * MINOR;
  return minor < 0 && major ? -major : major;
}

function groupDigits(int: string, sep: string): string {
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

export interface FormatOptions {
  locale: NumberLocale;
  /** Number of decimals to show (0 or 2). Defaults to 2. */
  decimals?: 0 | 2;
  /** 'auto' shows − for negatives only; 'always' also shows + for positives; 'never' drops the sign. */
  sign?: 'auto' | 'always' | 'never';
}

/** Format minor units as a plain grouped number: 1234500 → "12.345,00" (sr-Latn). */
export function formatNumber(minor: number, opts: FormatOptions): string {
  const { locale, decimals = 2, sign = 'auto' } = opts;
  if (!Number.isFinite(minor)) return '—';
  const sep = SEPARATORS[locale];
  // Callers sometimes pass derived values (chart ticks, averages), so fractions of a minor unit are rounded here.
  const abs = new Big(minor).abs().round(0, ROUND_HALF_UP);
  const body =
    decimals === 0
      ? groupDigits(abs.div(MINOR).round(0, ROUND_HALF_UP).toFixed(0), sep.group)
      : groupDigits(abs.div(MINOR).round(0, ROUND_DOWN).toFixed(0), sep.group) +
        sep.decimal +
        abs.mod(MINOR).toFixed(0).padStart(2, '0');
  const isZero = !/[1-9]/.test(body); // what is shown rounds to zero: no sign, never "−0"
  if (sign === 'never' || isZero) return body;
  if (minor < 0) return MINUS + body;
  return sign === 'always' ? '+' + body : body;
}

/** Minor units as a plain decimal string with a point: 284750 → "2847.50", −5 → "-0.05". */
export function minorToDecimalString(minor: number): string {
  return new Big(minor).round(0, ROUND_HALF_UP).div(MINOR).toFixed(2);
}

/** Format a currency amount: "12.450,00 RSD" / "€ 1.234,56" / "−€ 24,00". EUR always shows 2 decimals. */
export function formatMoney(minor: number, currency: Currency, opts: FormatOptions): string {
  if (currency === 'EUR') {
    const body = formatNumber(Math.abs(minor), { ...opts, decimals: 2, sign: 'never' });
    const isZero = minor === 0;
    const s = opts.sign ?? 'auto';
    const prefix = isZero || s === 'never' ? '' : minor < 0 ? MINUS : s === 'always' ? '+' : '';
    return `${prefix}€ ${body}`;
  }
  return `${formatNumber(minor, opts)} RSD`;
}

/** Format a scaled rate: 1_171_500 → "117,15". */
export function formatRate(rate4: number, locale: NumberLocale, decimals = 2): string {
  const sep = SEPARATORS[locale];
  const [i = '0', f] = new Big(rate4).div(RATE_SCALE).toFixed(decimals).split('.');
  return groupDigits(i, sep.group) + (f ? sep.decimal + f : '');
}

/** Format a percentage given as a fraction: 0.0042 → "0,42%". */
export function formatPercent(fraction: number, locale: NumberLocale, decimals = 2): string {
  const sep = SEPARATORS[locale];
  const s = new Big(Math.abs(fraction)).times(100).toFixed(decimals).replace('.', sep.decimal);
  return (fraction < 0 ? MINUS : '') + s + '%';
}

/**
 * Split a numeric string into integer and fraction digits. The locale's decimal separator is always a
 * decimal point. The other separator is a decimal point only when it appears once and is followed by
 * 1..maxFrac digits (so "24.5" typed in sr-Latn still means 24,50); otherwise it is grouping.
 */
function splitNumber(s: string, locale: NumberLocale, maxFrac: number): [string, string] {
  const sep = SEPARATORS[locale];
  const dec = s.lastIndexOf(sep.decimal);
  if (dec >= 0) return [s.slice(0, dec).split(sep.group).join(''), s.slice(dec + 1)];
  const first = s.indexOf(sep.group);
  const last = s.lastIndexOf(sep.group);
  const tail = s.slice(last + 1);
  if (first >= 0 && first === last && tail.length >= 1 && tail.length <= maxFrac && /^\d+$/.test(tail)) {
    return [s.slice(0, last), tail];
  }
  return [s.split(sep.group).join(''), ''];
}

const MINOR_DIGITS = 2;

/** "12" + "5" → 1250 with 2 decimals; null above MAX_MINOR. Pure decimal arithmetic, no float rounding. */
function digitsToMinor(intPart: string, fracPart: string, decimals: number): number | null {
  const value = new Big(`${intPart || '0'}.${fracPart.padEnd(decimals, '0') || '0'}`).times(10 ** decimals);
  return value.gt(MAX_MINOR) ? null : Number(value.toFixed(0));
}

/**
 * Parse a user-typed amount into minor units. Accepts the locale's grouping and decimal separators,
 * an optional leading sign (−, -, +) and surrounding currency text. Returns null when there are no digits.
 */
export function parseAmount(input: string, locale: NumberLocale): number | null {
  let s = input.trim().replace(/−/g, '-').replace(/\s/g, '');
  s = s.replace(/€|RSD|din\.?|eur/gi, '');
  if (!/\d/.test(s)) return null;
  const negative = s.startsWith('-');
  s = s.replace(/^[+-]/, '');
  if (!/^[\d.,]+$/.test(s)) return null;
  const [intPart, fracPart] = splitNumber(s, locale, 2);
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart) || fracPart.length > 2) return null;
  const minor = digitsToMinor(intPart, fracPart, MINOR_DIGITS);
  if (minor === null) return null;
  return negative ? -minor : minor;
}

/**
 * Evaluate what the user typed into an Assigned cell. "+500", "-1.000" and "*2" are relative to `current`.
 * Anything else replaces the value. Returns null when unparseable.
 */
export function evalAssignInput(input: string, current: number, locale: NumberLocale): number | null {
  const s = input.trim().replace(/−/g, '-');
  if (s.startsWith('*') || s.startsWith('×')) {
    const factor = parseAmount(s.slice(1), locale);
    if (factor === null) return null;
    return toMinor(new Big(current).times(factor).div(MINOR));
  }
  if (s.startsWith('+') || s.startsWith('-')) {
    const delta = parseAmount(s.slice(1), locale);
    if (delta === null) return null;
    return s.startsWith('+') ? current + delta : current - delta;
  }
  return parseAmount(s, locale);
}

/** Parse a rate typed by the user ("116,40") into the scaled integer form. */
export function parseRate(input: string, locale: NumberLocale): number | null {
  const s = input.trim().replace(/\s/g, '');
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return null;
  const [intPart, fracPart] = splitNumber(s, locale, 4);
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart) || fracPart.length > 4) return null;
  const rate4 = digitsToMinor(intPart, fracPart, 4);
  return rate4 !== null && rate4 > 0 ? rate4 : null;
}

/** Decimal string ("2847.50") → minor units. Used by the capture API. */
export function decimalStringToMinor(s: string): number | null {
  const m = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(s.trim());
  if (!m) return null;
  const minor = digitsToMinor(m[2]!, m[3] ?? '', MINOR_DIGITS);
  if (minor === null) return null;
  return m[1] ? -minor : minor;
}

/**
 * Live-input guard for money fields: keeps what the user typed but drops digits beyond `maxDecimals` after the
 * locale's decimal separator ("12,345" → "12,34" in sr-Latn). The other separator is left alone because it may be grouping.
 */
export function limitDecimals(text: string, locale: NumberLocale, maxDecimals = 2): string {
  const dec = SEPARATORS[locale].decimal;
  const at = text.lastIndexOf(dec);
  if (at < 0) return text;
  const tail = text.slice(at + 1);
  const digits = /^\d*/.exec(tail)![0];
  if (digits.length <= maxDecimals) return text;
  return text.slice(0, at + 1) + digits.slice(0, maxDecimals) + tail.slice(digits.length);
}
